import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import * as tConst from '../../Common/Constants';
import { AF_TITLE, Ticket } from '../../services/Ticket';
import { ProjectWorkItemData } from '../../services/AzureDevOpsService';
import ChartMenuForm from './ChartMenuForm';
import ChildrenMenuToolbar from './ChildrenMenuToolbar';
import ChartMenuToolbar from './ChartMenuToolbar';
import ParentChain from './ParentChain';

// Define the ChartMenuProps interface
interface ChartMenuProps {
  showChartMenu: boolean;
  selectedTicket: Ticket | null;
  menuTicketID: string;
  showTeams: boolean; // Optional prop to control visibility of teams
  onSetRefresh: () => void;
  handleClose: () => void;
  onMoveAndExtendIteration?: (ticket: Ticket, moveDays: number, extendDays: number) => void;
  onUpdateTicket?: (ticketId: number, updatedFields: Record<string, any>) => Promise<void>;
  onPlanningToChildren?: (ticket: Ticket | null) => void;
  RemoveFromTimeline: (ticketId: number) => void;
  AddPlanningTask: (selectedTicket: Ticket, spec: string) => void;
  useAdvancedMode: boolean; // New prop for advanced mode
  createTicket(project: string, ticketType: string, fields: Record<string, any>, parentUrl : string): Promise<any>
  projectWorkItemData: ProjectWorkItemData[];
  usersData: any[];
  capexOptions: string[];
  govProjectOptions: string[];
  priorityDriverOptions: string[];
  demoOptions: string[];
  productOptions: string[];
  investmentClassOptions: string[];
  areaPathOptions?: string[]; // Add area path options (optional, only for RnD Planning)
  azureDevOpsService: any; // Add AzureDevOpsService instance
  iterationsData: any[]; // Add iterations data
  selectedAreaPath?: string; // Selected area path filter (optional)
  onSelectTicket?: (ticket: Ticket) => void; // Callback to switch to a different ticket
}

const ChartMenu: React.FC<ChartMenuProps> = (props) => {
  const [refreshChildren, setRefreshChildren] = useState<number | null>(null);
  const [showComments, setShowComments] = useState<boolean>(false);
  const [showChildren, setShowChildren] = useState<boolean>(true);
  const [formHasChanges, setFormHasChanges] = useState<boolean>(false);
  const [periodHasChanges, setPeriodHasChanges] = useState<boolean>(false);
  const [formSaveFunction, setFormSaveFunction] = useState<(() => Promise<void>) | null>(null);
  const [periodSaveFunction, setPeriodSaveFunction] = useState<(() => void) | null>(null);
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [titleHasChanges, setTitleHasChanges] = useState<boolean>(false);

  // Initialize title when selectedTicket changes
  useEffect(() => {
    if (props.selectedTicket?.fields[AF_TITLE]) {
      setEditedTitle(props.selectedTicket.fields[AF_TITLE]);
      setTitleHasChanges(false);
    }
  }, [props.selectedTicket]);

  const handleTitleChange = (value: string) => {
    setEditedTitle(value);
    setTitleHasChanges(value !== (props.selectedTicket?.fields[AF_TITLE] || ''));
  };

  const handleCombinedSave = async () => {
    try {
      if (titleHasChanges && props.onUpdateTicket && props.selectedTicket) {
        console.log('Updating title from:', props.selectedTicket.fields[AF_TITLE], 'to:', editedTitle.trim());
        const updatedFields: Record<string, any> = {
          [AF_TITLE]: editedTitle.trim()
        };
        await props.onUpdateTicket(props.selectedTicket.id, updatedFields);
        console.log('Title update completed');
        if (props.selectedTicket.fields) {
          props.selectedTicket.fields[AF_TITLE] = editedTitle.trim();
        }
      }
      
      if (formHasChanges && formSaveFunction) {
        await formSaveFunction();
      }
      
      if (periodHasChanges && periodSaveFunction) {
        periodSaveFunction();
      }
      
      close(true);
    } catch (error) {
      console.error('Error during combined save:', error);
    }
  };

  const close = (refresh = false) => {
    if (refresh) {
      props.onSetRefresh();
    }
    props.handleClose();
  };

  if (props.showChartMenu && props.selectedTicket) {
    return (
      <Modal
        visible={props.showChartMenu}
        onCancel={() => close()}
        footer={null}
        width="80%"
      >
        <div>
          <ParentChain 
            selectedTicket={props.selectedTicket}
            azureDevOpsService={props.azureDevOpsService}
            onSelectTicket={props.onSelectTicket}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <a
              href={`${tConst.AZURE_DEVOPS_BASE_TICKET_URI}${props.menuTicketID}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '18px', fontWeight: 'bold' }}
            >
              {props.selectedTicket?.parent && props.selectedTicket.parent !== 0 ? '↳ ' : ''}[{props.menuTicketID}]
            </a>
            <Input
              value={editedTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              style={{ flex: 1, fontSize: '18px', fontWeight: 'bold' }}
              placeholder="Enter title"
            />
          </div>
          <ChartMenuToolbar
            selectedTicket={props.selectedTicket}
            onMoveAndExtendIteration={props.onMoveAndExtendIteration}
            RemoveFromTimeline= {props.RemoveFromTimeline}
            AddPlanningTask={props.AddPlanningTask}
            isLoaded={!!refreshChildren}
            onRefreshChildren={() => setRefreshChildren(Date.now())}
            onSetRefresh={props.onSetRefresh}
            onClose={() => close(true)}
            showComments={showComments}
            onToggleComments={() => setShowComments(!showComments)}
            showChildren={showChildren}
            onToggleChildren={() => setShowChildren(!showChildren)}
            onPeriodChangedStateChange={(hasChanges) => setPeriodHasChanges(hasChanges)}
            onExecutePeriodSave={(saveFunc) => setPeriodSaveFunction(() => saveFunc)}
            formHasChanges={formHasChanges}
            formSaveFunction={formSaveFunction}
          />
          <div>
            <ChartMenuForm
              showTeams={props.showTeams || false} // Use the prop or default to false
              selectedTicket={props.selectedTicket}
              onUpdateTicket={props.onUpdateTicket}
              onSetRefresh={props.onSetRefresh}
              onRefreshChildren={() => setRefreshChildren(Date.now())}
              onClose={() => close(true)}
              useAdvancedMode={props.useAdvancedMode} // Pass useAdvancedMode
              projectWorkItemData={props.projectWorkItemData}
              usersData={props.usersData}
              capexOptions={props.capexOptions}
              govProjectOptions={props.govProjectOptions}
              priorityDriverOptions={props.priorityDriverOptions}
              demoOptions={props.demoOptions}
              productOptions={props.productOptions}
              investmentClassOptions={props.investmentClassOptions}
              areaPathOptions={props.areaPathOptions}
              azureDevOpsService={props.azureDevOpsService}
              showComments={showComments}
              iterationsData={props.iterationsData}
              onFormChangedStateChange={setFormHasChanges}
              onExecuteFormSave={(saveFunc) => setFormSaveFunction(() => saveFunc)}
            />
          </div>
          <>
            <hr />
            <ChildrenMenuToolbar
            //FixMe: We should not pass isLoaded as true like this, but it is a temporary fix
              isLoaded={true}
              children={props.selectedTicket?.children || []} // Pass children if available
              selectedTicket={props.selectedTicket}
              onPlanningToChildren={props.onPlanningToChildren}
              onSetRefresh={() => setRefreshChildren(Date.now())}
              createTicket={props.createTicket}
              onClose={() => close(true)}
              selectedAreaPath={props.selectedAreaPath}
              onSelectTicket={props.onSelectTicket}
              azureDevOpsService={props.azureDevOpsService}
              showChildren={showChildren}
              onToggleChildren={() => setShowChildren(!showChildren)}
            />
          </>
        </div>
        <div style={{ textAlign: 'right', marginTop: '20px' }}>
          {(formHasChanges || periodHasChanges || titleHasChanges) ? (
            <>
              <Button onClick={() => close()}>
                Cancel
              </Button>
              &nbsp;
              <Button 
                type="primary" 
                onClick={handleCombinedSave}
              >
                <SaveOutlined /> Save
              </Button>
            </>
          ) : (
            <Button type="primary" onClick={() => close()}>
              Close
            </Button>
          )}
        </div>
      </Modal>
    );
  } else {
    return null;
  }
};

export default ChartMenu;
